import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Crud_Catalogos } from "../../herramientas/Crud_Catalogos";
import Select from "react-select";
import Chart from "react-apexcharts";
import SiteWrapper from "../../SiteWrapper.react";
import axios from "axios";
import * as conf from "../../config/config";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

const GpsPatiosCirculacion = () => {
  const [gpsList, setGpsList] = useState([]);
  const [gpsFijosList, setGpsFijosList] = useState([]);
  const [gpsDisponibles, setGpsDisponibles] = useState([]);
  const [gpsSeleccionado, setGpsSeleccionado] = useState(null);
  const clearTimerRef = useRef(null);
  
  // Filtros
  const [localidadOpcion, setLocalidadOpcion] = useState({ value: 0, label: "Todos" });
  const [paisOpcion, setPaisOpcion] = useState({ value: 0, label: "Todos" });
  const [plataformaOpcion, setPlataformaOpcion] = useState({ value: 0, label: "Todos" });
  
  // Buscador de transportistas
  const [busquedaTransportista, setBusquedaTransportista] = useState("");
  
  // Paginación para transportistas
  const [paginaActual, setPaginaActual] = useState(1);
  const transportistasPorPagina = 20;

  // Cargar datos inicial
  useEffect(() => {
    getfuntion("get", [], "", "gps/dash", "patios_circulacion");
    cargarGpsDisponibles();
    startAutoRefresh();

    // Cleanup al desmontar el componente
    return () => {
      if (clearTimerRef.current) {
        clearInterval(clearTimerRef.current);
        clearTimerRef.current = null;
      }
    };
  }, []);

  // Función para iniciar recarga automática cada minuto
  const startAutoRefresh = () => {
    if (clearTimerRef.current) {
      clearInterval(clearTimerRef.current);
      clearTimerRef.current = null;
    }

    clearTimerRef.current = setInterval(() => {
      getfuntion("get", [], "", "gps/dash", "patios_circulacion");
    }, 60000); // 60 segundos
  };

  // Memoizar opciones de filtros para evitar recalcular en cada render
  const opcionesFiltros = useMemo(() => {
    const localidades = new Set();
    const paises = new Set();
    const plataformas = new Set();

    gpsList.forEach((gps) => {
      if (gps.corredor && gps.corredor !== 'Sin corredor') {
        localidades.add(gps.corredor);
      }
      if (gps.pais && gps.pais !== 'Sin país') {
        paises.add(gps.pais);
      }
      if (gps.plataforma && gps.plataforma !== 'Sin plataforma') {
        plataformas.add(gps.plataforma);
      }
    });

    return {
      localidadOpciones: [
        { value: 0, label: "Todos" },
        ...Array.from(localidades).map(item => ({ value: item, label: item }))
      ],
      paisOpciones: [
        { value: 0, label: "Todos" },
        ...Array.from(paises).map(item => ({ value: item, label: item }))
      ],
      plataformaOpciones: [
        { value: 0, label: "Todos" },
        ...Array.from(plataformas).map(item => ({ value: item, label: item }))
      ]
    };
  }, [gpsList]);

  // Memoizar datos filtrados
  const gpsListFiltrados = useMemo(() => {
    let gpssTemp = [...gpsList];

    if (localidadOpcion.value) {
      gpssTemp = gpssTemp.filter(g => g.corredor === localidadOpcion.label);
    }
    if (paisOpcion.value) {
      gpssTemp = gpssTemp.filter(g => g.pais === paisOpcion.label);
    }
    if (plataformaOpcion.value) {
      gpssTemp = gpssTemp.filter(g => g.plataforma === plataformaOpcion.label);
    }

    return gpssTemp;
  }, [gpsList, localidadOpcion, paisOpcion, plataformaOpcion]);

  // Funciones de mapeo para exportar Excel con columnas específicas
  const exportarDesdeEndpoint = (endpoint, fileName) => {
    const token = sessionStorage.getItem('tok');
    axios.get(`${conf.api_raiz}gps/dash/${endpoint}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    .then(response => {
      const data = response.data.map(item => typeof item === 'string' ? JSON.parse(item) : item);
      if (data.length === 0) {
        alert("No hay datos para exportar");
        return;
      }
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = { Sheets: { datos: ws }, SheetNames: ["datos"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
      FileSaver.saveAs(blob, fileName + ".xlsx");
    })
    .catch(err => {
      alert("Error al exportar datos");
    });
  };

  // Función optimizada para limpiar filtros
  const limpiarFiltros = useCallback(() => {
    const filtrosDefault = { value: 0, label: "Todos" };
    setLocalidadOpcion(filtrosDefault);
    setPaisOpcion(filtrosDefault);
    setPlataformaOpcion(filtrosDefault);
  }, []);

  // Función reutilizable para calcular color y categoría según días sin reporte
  const calcularSemaforo = (gps, fechaActual) => {
    let diasSinReporte = null;
    let color = null;
    let categoriaDias = null;

    // Para GPS "Por Recuperar", usar endDate; para otros, usar deviceactivity
    const fechaReferencia = gps.gpsEstatus === 'Por Recuperar' ? gps.endDate : gps.deviceactivity;

    if (fechaReferencia) {
      const fechaDevice = new Date(fechaReferencia).getTime();
      diasSinReporte = Math.round((fechaActual - fechaDevice) / (1000 * 60 * 60 * 24));

      if (gps.gpsEstatus !== 'Instalado') {
        if (diasSinReporte <= 2) {
          color = 'green';
          categoriaDias = '0-2 días';
        } else if (diasSinReporte <= 10) {
          color = 'yellow';
          categoriaDias = '3-10 días';
        } else {
          color = 'red';
          categoriaDias = 'Más de 10 días';
        }
      }
    } else {
      if (gps.gpsEstatus !== 'Instalado') {
        color = 'gray';
        categoriaDias = 'Sin reporte';
      }
    }

    return { diasSinReporte, color, categoriaDias };
  };

  const getfuntion = (metodo = "get", obj = [], id = "", catalogo = "", stateVar = "") => {
    Crud_Catalogos(catalogo, "home", metodo, id, obj, "", stateVar, "", [])
      .then((returnVal) => {
        if (metodo === "get" && stateVar === "patios_circulacion") {
          if (returnVal) {
            const fechaActual = new Date().getTime();

            // GPS Portátiles (gpsFijo = false/null)
            const gpsListProcesada = returnVal
              .filter(gps => !gps.gpsFijo)
              .map(gps => ({
                ...gps,
                ...calcularSemaforo(gps, fechaActual)
              }));

            setGpsList(gpsListProcesada);

            // GPS Fijos (gpsFijo = true)
            const gpsFijosProcesada = returnVal
              .filter(gps => gps.gpsFijo)
              .map(gps => ({
                ...gps,
                ...calcularSemaforo(gps, fechaActual)
              }));

            setGpsFijosList(gpsFijosProcesada);
          }
        }
      })
      .catch((err) => {});
  };

  // Cargar GPS disponibles (todos los GPS para poder filtrar)
  const cargarGpsDisponibles = () => {
    Crud_Catalogos("gps", "home", "get", "", [], "", "", "", [])
      .then((returnVal) => {
        if (returnVal) {
          const gpsNoPatios = returnVal.filter(gps => !gps.patios || gps.patios === 0);
          const opciones = gpsNoPatios.map(gps => ({
            value: gps.id,
            label: `${gps.nombre} - ${gps.imei}`
          }));
          setGpsDisponibles(opciones);
        }
      })
      .catch((err) => {});
  };

  // Agregar GPS a patios (patios = true)
  const agregarGpsPatios = () => {
    if (!gpsSeleccionado) {
      alert("Selecciona un GPS");
      return;
    }

    const token = sessionStorage.getItem('tok');
    
    axios.put(`${conf.api_raiz}gps/patios/${gpsSeleccionado.value}?patios=true`, null, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      alert("GPS agregado a patios correctamente");
      setGpsSeleccionado(null);
      getfuntion("get", [], "", "gps/dash", "patios_circulacion");
      cargarGpsDisponibles();
    })
    .catch(error => {
      alert(`Error al agregar GPS a patios: ${error.response?.data || error.message}`);
    });
  };

  // Eliminar GPS de patios (patios = false)
  const eliminarGpsPatios = (gpsId, gpsNombre) => {
    if (!window.confirm(`¿Estás seguro de eliminar ${gpsNombre} de patios?`)) {
      return;
    }

    const token = sessionStorage.getItem('tok');
    
    axios.put(`${conf.api_raiz}gps/patios/${gpsId}?patios=false`, null, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      alert("GPS eliminado de patios correctamente");
      getfuntion("get", [], "", "gps/dash", "patios_circulacion");
      cargarGpsDisponibles();
    })
    .catch(error => {
      alert("Error al eliminar GPS de patios");
    });
  };

  // Memoizar cálculos de datos para evitar recalcular en cada render
  const datosCalculados = useMemo(() => {
    const disponibles = gpsListFiltrados.filter(g => g.gpsEstatus === 'Disponible');
    const instalados = gpsListFiltrados.filter(g => g.gpsEstatus === 'Instalado');
    const porRecuperar = gpsListFiltrados.filter(g => g.gpsEstatus === 'Por Recuperar');

    // Calcular datos de disponibles
    const disponiblesData = {
      total: disponibles.length,
      porDias: {
        verde: disponibles.filter(g => g.color === 'green').length,
        amarillo: disponibles.filter(g => g.color === 'yellow').length,
        rojo: disponibles.filter(g => g.color === 'red').length,
        sinReporte: disponibles.filter(g => g.color === 'gray').length
      },
      porLocalidad: {}
    };

    disponibles.forEach(gps => {
      const loc = gps.corredor || 'Sin localidad';
      if (!disponiblesData.porLocalidad[loc]) {
        disponiblesData.porLocalidad[loc] = { total: 0, verde: 0, amarillo: 0, rojo: 0, gris: 0 };
      }
      disponiblesData.porLocalidad[loc].total++;
      if (gps.color === 'green') disponiblesData.porLocalidad[loc].verde++;
      if (gps.color === 'yellow') disponiblesData.porLocalidad[loc].amarillo++;
      if (gps.color === 'red') disponiblesData.porLocalidad[loc].rojo++;
      if (gps.color === 'gray') disponiblesData.porLocalidad[loc].gris++;
    });

    // Calcular datos de instalados
    const instaladosData = {
      total: instalados.length,
      porPlataforma: {}
    };

    instalados.forEach(gps => {
      const plat = gps.plataforma || 'Sin plataforma';
      instaladosData.porPlataforma[plat] = (instaladosData.porPlataforma[plat] || 0) + 1;
    });

    // Calcular datos de por recuperar
    const porRecuperarData = {
      total: porRecuperar.length,
      porDias: {
        verde: porRecuperar.filter(g => g.color === 'green').length,
        amarillo: porRecuperar.filter(g => g.color === 'yellow').length,
        rojo: porRecuperar.filter(g => g.color === 'red').length,
        sinReporte: porRecuperar.filter(g => g.color === 'gray').length
      },
      porTransportista: {}
    };

    porRecuperar.forEach(gps => {
      const trans = gps.transportista || 'Sin transportista';
      if (!porRecuperarData.porTransportista[trans]) {
        porRecuperarData.porTransportista[trans] = { total: 0, verde: 0, amarillo: 0, rojo: 0, gris: 0 };
      }
      porRecuperarData.porTransportista[trans].total++;
      if (gps.color === 'green') porRecuperarData.porTransportista[trans].verde++;
      if (gps.color === 'yellow') porRecuperarData.porTransportista[trans].amarillo++;
      if (gps.color === 'red') porRecuperarData.porTransportista[trans].rojo++;
      if (gps.color === 'gray') porRecuperarData.porTransportista[trans].gris++;
    });

    // Ordenar transportistas por total de unidades (mayor a menor)
    const transportistasOrdenados = Object.entries(porRecuperarData.porTransportista)
      .sort((a, b) => b[1].total - a[1].total);

    // Filtrar por búsqueda si hay texto
    const transportistasFiltrados = busquedaTransportista 
      ? transportistasOrdenados.filter(([nombre]) => 
          nombre.toLowerCase().includes(busquedaTransportista.toLowerCase())
        )
      : transportistasOrdenados;

    // Paginación
    const totalPaginas = Math.ceil(transportistasFiltrados.length / transportistasPorPagina);
    const inicio = (paginaActual - 1) * transportistasPorPagina;
    const fin = inicio + transportistasPorPagina;
    const transportistasPaginados = transportistasFiltrados.slice(inicio, fin);

    return {
      disponibles: disponiblesData,
      instalados: instaladosData,
      porRecuperar: {
        ...porRecuperarData,
        transportistasOrdenados,
        transportistasFiltrados,
        transportistasPaginados,
        totalPaginas,
        paginaActual
      }
    };
  }, [gpsListFiltrados, busquedaTransportista, paginaActual]);

  // Datos calculados para GPS Fijos
  const datosFijos = useMemo(() => {
    const disponibles = gpsFijosList.filter(g => g.gpsEstatus === 'Disponible');
    const instalados = gpsFijosList.filter(g => g.gpsEstatus === 'Instalado');

    const disponiblesData = {
      total: disponibles.length,
      porDias: {
        verde: disponibles.filter(g => g.color === 'green').length,
        amarillo: disponibles.filter(g => g.color === 'yellow').length,
        rojo: disponibles.filter(g => g.color === 'red').length,
        sinReporte: disponibles.filter(g => g.color === 'gray').length
      },
      porTransportista: {}
    };

    disponibles.forEach(gps => {
      const trans = gps.transportista || 'Sin transportista';
      if (!disponiblesData.porTransportista[trans]) {
        disponiblesData.porTransportista[trans] = { total: 0, verde: 0, amarillo: 0, rojo: 0, gris: 0 };
      }
      disponiblesData.porTransportista[trans].total++;
      if (gps.color === 'green') disponiblesData.porTransportista[trans].verde++;
      if (gps.color === 'yellow') disponiblesData.porTransportista[trans].amarillo++;
      if (gps.color === 'red') disponiblesData.porTransportista[trans].rojo++;
      if (gps.color === 'gray') disponiblesData.porTransportista[trans].gris++;
    });

    const instaladosData = {
      total: instalados.length,
      porPlataforma: {}
    };

    instalados.forEach(gps => {
      const plat = gps.plataforma || 'Sin plataforma';
      instaladosData.porPlataforma[plat] = (instaladosData.porPlataforma[plat] || 0) + 1;
    });

    return {
      total: gpsFijosList.length,
      disponibles: disponiblesData,
      instalados: instaladosData
    };
  }, [gpsFijosList]);

  return (
    <SiteWrapper>
      <div className="container-fluid">
        {/* Título */}
        <div className="row justify-content-center" style={{ margin: "16px" }}>
          <div className="col-12 text-center">
            <h1>Circulación de activos - GPS Patios</h1>
            <p className="text-muted">Dashboard de monitoreo de GPS | Total universo: {gpsListFiltrados.length + gpsFijosList.length} dispositivos</p>
          </div>
        </div>

        {/* Filtros */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="form-group">
              <label><strong>Localidad</strong></label>
              <Select
                value={localidadOpcion}
                onChange={setLocalidadOpcion}
                options={opcionesFiltros.localidadOpciones}
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label><strong>País</strong></label>
              <Select
                value={paisOpcion}
                onChange={setPaisOpcion}
                options={opcionesFiltros.paisOpciones}
              />
            </div>
          </div>
          <div className="col-md-3">
            <div className="form-group">
              <label><strong>Plataforma</strong></label>
              <Select
                value={plataformaOpcion}
                onChange={setPlataformaOpcion}
                options={opcionesFiltros.plataformaOpciones}
              />
            </div>
          </div>
          <div className="col-md-3 d-flex align-items-end">
            <button 
              className="btn btn-secondary btn-block"
              onClick={limpiarFiltros}
            >
              Limpiar filtros
            </button>
          </div>
        </div>
        
        {/* Resumen general */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5>GPS Portátiles</h5>
                <div className="row text-center">
                  <div className="col-md-3">
                    <h3 className="text-success">{datosCalculados.disponibles.total}</h3>
                    <p>Disponibles</p>
                  </div>
                  <div className="col-md-3">
                    <h3 className="text-primary">{datosCalculados.instalados.total}</h3>
                    <p>Instalados</p>
                  </div>
                  <div className="col-md-3">
                    <h3 className="text-warning">{datosCalculados.porRecuperar.total}</h3>
                    <p>Por Recuperar</p>
                  </div>
                  <div className="col-md-3">
                    <h3 className="text-info">{gpsListFiltrados.length}</h3>
                    <p>Total Portátiles</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Dashboard principal - 3 columnas */}
        <div className="row">
          {/* COLUMNA 1: DISPONIBLES */}
          <div className="col-md-4">
            <div className="card mb-4 shadow-sm">
              <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fe fe-check-circle mr-2"></i>
                  Disponibles
                </h4>
                <button
                  className="btn btn-info"
                  title="Exportar a Excel"
                  onClick={() => exportarDesdeEndpoint('disponibles_patios', 'GPS_Disponibles')}
                >
                  Export
                </button>
              </div>
              <div className="card-body">
                <h2 className="text-center mb-3">{datosCalculados.disponibles.total}</h2>
                
                {/* Gráfica de dona - por días */}
                {datosCalculados.disponibles.total > 0 ? (
                  <>
                    <Chart
                      series={[
                        datosCalculados.disponibles.porDias.verde,
                        datosCalculados.disponibles.porDias.amarillo,
                        datosCalculados.disponibles.porDias.rojo,
                        datosCalculados.disponibles.porDias.sinReporte
                      ]}
                      options={{
                        chart: { type: 'donut' },
                        labels: ['0-2 días', '3-10 días', 'Más de 10 días', 'Sin reporte'],
                        colors: ['#28a745', '#ffc107', '#dc3545', '#6c757d'],
                        legend: { position: 'bottom' },
                        dataLabels: {
                          enabled: true,
                          formatter: function (val) {
                            return val.toFixed(0) + "%"
                          }
                        }
                      }}
                      type="donut"
                      height={250}
                    />

                    {/* Gráfica de barras horizontales - por localidad */}
                    {Object.keys(datosCalculados.disponibles.porLocalidad).length > 0 && (() => {
                      // Ordenar localidades por total de GPS (mayor a menor)
                      const localidadesOrdenadas = Object.entries(datosCalculados.disponibles.porLocalidad)
                        .sort((a, b) => b[1].total - a[1].total);
                      
                      return (
                        <div className="mt-4">
                          <h6>Por Localidad</h6>
                          <Chart
                            series={[
                              {
                                name: '0-2 días',
                                data: localidadesOrdenadas.map(([, data]) => data.verde)
                              },
                              {
                                name: '3-10 días',
                                data: localidadesOrdenadas.map(([, data]) => data.amarillo)
                              },
                              {
                                name: 'Más de 10 días',
                                data: localidadesOrdenadas.map(([, data]) => data.rojo)
                              },
                              {
                                name: 'Sin reporte',
                                data: localidadesOrdenadas.map(([, data]) => data.gris)
                              }
                            ]}
                            options={{
                              chart: { 
                                type: 'bar', 
                                stacked: true,
                                stackType: '100%',
                                toolbar: { show: false }
                              },
                              plotOptions: {
                                bar: { 
                                  horizontal: true,
                                  barHeight: '70%',
                                  dataLabels: {
                                    position: 'center',
                                    total: {
                                      enabled: false
                                    }
                                  }
                                }
                              },
                              dataLabels: {
                                enabled: true,
                                formatter: function (val, opts) {
                                  return opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex]
                                }
                              },
                              xaxis: {
                                categories: localidadesOrdenadas.map(([nombre]) => nombre),
                                labels: { show: false }
                              },
                              yaxis: {
                                labels: {
                                  style: {
                                    fontSize: '10px'
                                  },
                                  maxWidth: 200,
                                  trim: false
                                }
                              },
                              tooltip: {
                                y: {
                                  formatter: (val, { seriesIndex, dataPointIndex }) => {
                                    const loc = localidadesOrdenadas[dataPointIndex];
                                    return val + ' GPS (Total: ' + loc[1].total + ')';
                                  }
                                }
                              },
                              colors: ['#28a745', '#ffc107', '#dc3545', '#6c757d'],
                              legend: {
                                position: 'bottom',
                                fontSize: '11px'
                              }
                            }}
                            type="bar"
                            height={Math.max(150, localidadesOrdenadas.length * 40)}
                          />
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <p className="text-center text-muted">No hay GPS disponibles</p>
                )}
              </div>
            </div>
          </div>

          {/* COLUMNA 2: INSTALADOS */}
          <div className="col-md-4">
            <div className="card mb-4 shadow-sm">
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fe fe-map-pin mr-2"></i>
                  Instalados
                </h4>
                <button
                  className="btn btn-info"
                  title="Exportar a Excel"
                  onClick={() => exportarDesdeEndpoint('instalados_patios', 'GPS_Instalados')}
                >
                  Export
                </button>
              </div>
              <div className="card-body">
                <h1 className="display-3 text-primary text-center">{datosCalculados.instalados.total}</h1>
                <p className="text-muted text-center">GPS activos</p>

                {/* Gráfica de dona - por plataforma */}
                {datosCalculados.instalados.total > 0 && Object.keys(datosCalculados.instalados.porPlataforma).length > 0 && (
                  <div className="mt-4">
                    <h6>Plataformas</h6>
                    <Chart
                      series={Object.values(datosCalculados.instalados.porPlataforma)}
                      options={{
                        chart: { type: 'donut' },
                        labels: Object.keys(datosCalculados.instalados.porPlataforma),
                        legend: { position: 'bottom' },
                        dataLabels: {
                          enabled: true,
                          formatter: function (val) {
                            return val.toFixed(1) + "%"
                          }
                        }
                      }}
                      type="donut"
                      height={300}
                    />

                    {/* Lista de plataformas con porcentajes y totales */}
                    <div className="mt-3">
                      <ul className="list-unstyled">
                        {Object.entries(datosCalculados.instalados.porPlataforma)
                          .sort((a, b) => b[1] - a[1])
                          .map(([plataforma, cantidad]) => {
                            const porcentaje = ((cantidad / datosCalculados.instalados.total) * 100).toFixed(1);
                            return (
                              <li key={plataforma} className="mb-2 d-flex justify-content-between">
                                <span>• {plataforma}</span>
                                <strong>{porcentaje}% ({cantidad})</strong>
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* COLUMNA 3: POR RECUPERAR */}
          <div className="col-md-4">
            <div className="card mb-4 shadow-sm">
              <div className="card-header bg-warning text-dark d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fe fe-alert-triangle mr-2"></i>
                  Por recuperar
                </h4>
                <button
                  className="btn btn-info"
                  title="Exportar a Excel"
                  onClick={() => exportarDesdeEndpoint('por_recuperar_patios', 'GPS_Por_Recuperar')}
                >
                  Export
                </button>
              </div>
              <div className="card-body">
                <h2 className="text-center mb-3">{datosCalculados.porRecuperar.total}</h2>
                
                {/* Gráfica de dona - por días */}
                {datosCalculados.porRecuperar.total > 0 ? (
                  <>
                    <Chart
                      series={[
                        datosCalculados.porRecuperar.porDias.verde,
                        datosCalculados.porRecuperar.porDias.amarillo,
                        datosCalculados.porRecuperar.porDias.rojo,
                        datosCalculados.porRecuperar.porDias.sinReporte
                      ]}
                      options={{
                        chart: { type: 'donut' },
                        labels: ['0-2 días', '3-10 días', 'Más de 10 días', 'Sin reporte'],
                        colors: ['#28a745', '#ffc107', '#dc3545', '#6c757d'],
                        legend: { position: 'bottom' },
                        dataLabels: {
                          enabled: true,
                          formatter: function (val) {
                            return val.toFixed(0) + "%"
                          }
                        }
                      }}
                      type="donut"
                      height={250}
                    />

                    {/* Gráfica de barras horizontales - por transportista */}
                    {datosCalculados.porRecuperar.transportistasOrdenados.length > 0 && (
                      <div className="mt-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6>Por Transportista</h6>
                          <small className="text-muted">
                            Página {datosCalculados.porRecuperar.paginaActual} de {datosCalculados.porRecuperar.totalPaginas} 
                            ({datosCalculados.porRecuperar.transportistasFiltrados.length} total)
                          </small>
                        </div>
                        
                        {/* Buscador */}
                        <div className="mb-3">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Buscar transportista..."
                            value={busquedaTransportista}
                            onChange={(e) => {
                              setBusquedaTransportista(e.target.value);
                              setPaginaActual(1); // Reset a página 1 al buscar
                            }}
                          />
                        </div>
                        
                        {/* Mensaje cuando no se encuentra */}
                        {busquedaTransportista && datosCalculados.porRecuperar.transportistasFiltrados.length === 0 ? (
                          <div className="text-center text-muted py-4">
                            <i className="fe fe-search mb-2" style={{fontSize: '2rem'}}></i>
                            <p>Transportista no localizado</p>
                            <small>No se encontró "{busquedaTransportista}" en el dashboard</small>
                          </div>
                        ) : datosCalculados.porRecuperar.transportistasPaginados.length > 0 && (
                          <>
                            <Chart
                              series={[
                                {
                                  name: '0-2 días',
                                  data: datosCalculados.porRecuperar.transportistasPaginados.map(([, data]) => data.verde)
                                },
                                {
                                  name: '3-10 días',
                                  data: datosCalculados.porRecuperar.transportistasPaginados.map(([, data]) => data.amarillo)
                                },
                                {
                                  name: 'Más de 10 días',
                                  data: datosCalculados.porRecuperar.transportistasPaginados.map(([, data]) => data.rojo)
                                },
                                {
                                  name: 'Sin reporte',
                                  data: datosCalculados.porRecuperar.transportistasPaginados.map(([, data]) => data.gris)
                                }
                              ]}
                              options={{
                                chart: { 
                                  type: 'bar', 
                                  stacked: true,
                                  stackType: '100%',
                                  toolbar: { show: false }
                                },
                                plotOptions: {
                                  bar: { 
                                    horizontal: true,
                                    barHeight: '70%',
                                    dataLabels: {
                                      position: 'center',
                                      total: {
                                        enabled: false
                                      }
                                    }
                                  }
                                },
                                dataLabels: {
                                  enabled: true,
                                  formatter: function (val, opts) {
                                    return opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex]
                                  }
                                },
                                xaxis: {
                                  categories: datosCalculados.porRecuperar.transportistasPaginados.map(([nombre]) => nombre),
                                  labels: { show: false }
                                },
                                yaxis: {
                                  labels: {
                                    style: {
                                      fontSize: '10px'
                                    },
                                    maxWidth: 200,
                                    trim: false
                                  }
                                },
                                tooltip: {
                                  y: {
                                    formatter: (val, { dataPointIndex }) => {
                                      const trans = datosCalculados.porRecuperar.transportistasPaginados[dataPointIndex];
                                      return val + ' GPS (Total: ' + trans[1].total + ')';
                                    }
                                  }
                                },
                                colors: ['#28a745', '#ffc107', '#dc3545', '#6c757d'],
                                legend: {
                                  position: 'bottom',
                                  fontSize: '11px'
                                }
                              }}
                              type="bar"
                              height={Math.max(300, datosCalculados.porRecuperar.transportistasPaginados.length * 40)}
                            />
                            
                            {/* Controles de paginación */}
                            {datosCalculados.porRecuperar.totalPaginas > 1 && (
                              <div className="d-flex justify-content-center align-items-center mt-3">
                                {paginaActual > 1 && (
                                  <button 
                                    className="btn btn-sm btn-outline-secondary me-2" 
                                    onClick={() => setPaginaActual(paginaActual - 1)}
                                  >
                                    -
                                  </button>
                                )}
                                <span className="mx-3">
                                  {paginaActual}
                                </span>
                                {paginaActual < datosCalculados.porRecuperar.totalPaginas && (
                                  <button 
                                    className="btn btn-sm btn-outline-secondary ms-2" 
                                    onClick={() => setPaginaActual(paginaActual + 1)}
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-center text-muted">No hay GPS por recuperar</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ===== DASHBOARD GPS FIJOS ===== */}
        <div className="row justify-content-center mt-5">
          <div className="col-12 text-center">
            <hr />
            <h2 className="mt-4">GPS Fijos</h2>
            <p className="text-muted">Dispositivos instalados permanentemente en chasis ({datosFijos.total} total)</p>
          </div>
        </div>

        {/* Resumen GPS Fijos */}
        <div className="row mt-3">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-md-4">
                    <h3 className="text-success">{datosFijos.disponibles.total}</h3>
                    <p>Disponibles</p>
                  </div>
                  <div className="col-md-4">
                    <h3 className="text-primary">{datosFijos.instalados.total}</h3>
                    <p>Instalados</p>
                  </div>
                  <div className="col-md-4">
                    <h3 className="text-info">{datosFijos.total}</h3>
                    <p>Total GPS Fijos</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard GPS Fijos - 2 columnas */}
        <div className="row">
          {/* COLUMNA 1: DISPONIBLES FIJOS */}
          <div className="col-md-6">
            <div className="card mb-4 shadow-sm">
              <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fe fe-check-circle mr-2"></i>
                  Disponibles (Fijos)
                </h4>
                <button
                  className="btn btn-info"
                  title="Exportar a Excel"
                  onClick={() => exportarDesdeEndpoint('disponibles_fijos_patios', 'GPS_Fijos_Disponibles')}
                >
                  Export
                </button>
              </div>
              <div className="card-body">
                <h2 className="text-center mb-3">{datosFijos.disponibles.total}</h2>
                
                {datosFijos.disponibles.total > 0 ? (
                  <>
                    <Chart
                      series={[
                        datosFijos.disponibles.porDias.verde,
                        datosFijos.disponibles.porDias.amarillo,
                        datosFijos.disponibles.porDias.rojo,
                        datosFijos.disponibles.porDias.sinReporte
                      ]}
                      options={{
                        chart: { type: 'donut' },
                        labels: ['0-2 días', '3-10 días', 'Más de 10 días', 'Sin reporte'],
                        colors: ['#28a745', '#ffc107', '#dc3545', '#6c757d'],
                        legend: { position: 'bottom' },
                        dataLabels: {
                          enabled: true,
                          formatter: function (val) {
                            return val.toFixed(0) + "%"
                          }
                        }
                      }}
                      type="donut"
                      height={250}
                    />

                    {/* Gráfica de barras por transportista */}
                    {Object.keys(datosFijos.disponibles.porTransportista).length > 0 && (() => {
                      const transportistasOrdenadas = Object.entries(datosFijos.disponibles.porTransportista)
                        .sort((a, b) => b[1].total - a[1].total);
                      
                      return (
                        <div className="mt-4">
                          <h6>Por Transportista</h6>
                          <Chart
                            series={[
                              { name: '0-2 días', data: transportistasOrdenadas.map(([, data]) => data.verde) },
                              { name: '3-10 días', data: transportistasOrdenadas.map(([, data]) => data.amarillo) },
                              { name: 'Más de 10 días', data: transportistasOrdenadas.map(([, data]) => data.rojo) },
                              { name: 'Sin reporte', data: transportistasOrdenadas.map(([, data]) => data.gris) }
                            ]}
                            options={{
                              chart: { type: 'bar', stacked: true, stackType: '100%', toolbar: { show: false } },
                              plotOptions: { bar: { horizontal: true, barHeight: '70%' } },
                              dataLabels: {
                                enabled: true,
                                formatter: function (val, opts) {
                                  return opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex]
                                }
                              },
                              xaxis: { categories: transportistasOrdenadas.map(([nombre]) => nombre), labels: { show: false } },
                              yaxis: { labels: { style: { fontSize: '10px' }, maxWidth: 200, trim: false } },
                              tooltip: {
                                y: {
                                  formatter: (val, { dataPointIndex }) => {
                                    const trans = transportistasOrdenadas[dataPointIndex];
                                    return val + ' GPS (Total: ' + trans[1].total + ')';
                                  }
                                }
                              },
                              colors: ['#28a745', '#ffc107', '#dc3545', '#6c757d'],
                              legend: { position: 'bottom', fontSize: '11px' }
                            }}
                            type="bar"
                            height={Math.max(150, transportistasOrdenadas.length * 40)}
                          />
                        </div>
                      );
                    })()}
                  </>
                ) : (
                  <p className="text-center text-muted">No hay GPS fijos disponibles</p>
                )}
              </div>
            </div>
          </div>

          {/* COLUMNA 2: INSTALADOS FIJOS */}
          <div className="col-md-6">
            <div className="card mb-4 shadow-sm">
              <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <i className="fe fe-map-pin mr-2"></i>
                  Instalados (Fijos)
                </h4>
                <button
                  className="btn btn-info"
                  title="Exportar a Excel"
                  onClick={() => exportarDesdeEndpoint('instalados_fijos_patios', 'GPS_Fijos_Instalados')}
                >
                  Export
                </button>
              </div>
              <div className="card-body">
                <h1 className="display-3 text-primary text-center">{datosFijos.instalados.total}</h1>
                <p className="text-muted text-center">GPS fijos activos</p>

                {datosFijos.instalados.total > 0 && Object.keys(datosFijos.instalados.porPlataforma).length > 0 && (
                  <div className="mt-4">
                    <h6>Plataformas</h6>
                    <Chart
                      series={Object.values(datosFijos.instalados.porPlataforma)}
                      options={{
                        chart: { type: 'donut' },
                        labels: Object.keys(datosFijos.instalados.porPlataforma),
                        legend: { position: 'bottom' },
                        dataLabels: {
                          enabled: true,
                          formatter: function (val) {
                            return val.toFixed(1) + "%"
                          }
                        }
                      }}
                      type="donut"
                      height={300}
                    />

                    <div className="mt-3">
                      <ul className="list-unstyled">
                        {Object.entries(datosFijos.instalados.porPlataforma)
                          .sort((a, b) => b[1] - a[1])
                          .map(([plataforma, cantidad]) => {
                            const porcentaje = ((cantidad / datosFijos.instalados.total) * 100).toFixed(1);
                            return (
                              <li key={plataforma} className="mb-2 d-flex justify-content-between">
                                <span>• {plataforma}</span>
                                <strong>{porcentaje}% ({cantidad})</strong>
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>


      </div>
    </SiteWrapper>
  );
};

export default GpsPatiosCirculacion;